/**
 * The gain map of an iPhone HEIC (1.181.0, #155).
 *
 * From use: "Umwandlung in ein HDR JPEG, das auch auf nicht iOS Geräten
 * angezeigt werden kann". An HDR photo from an iPhone since iOS 18 is a HEIF
 * with three things in it, measured at two of its photos
 * (`medien/iphone-hdr.heic`, IMG_9811 from use):
 *
 * - the primary picture, a grid of HEVC tiles in Display P3, 8 bit;
 * - the gain map as an auxiliary picture of the primary
 *   (`urn:com:apple:photo:2020:aux:hdrgainmap`), a grid of its own at half
 *   the size, one channel, 8 bit;
 * - a `tmap` item derived from both (`dimg` → primary, gain map), whose data
 *   is one version byte and then the metadata of ISO 21496-1, the same bytes
 *   an Ultra HDR JPEG carries in its APP2 segment.
 *
 * So the conversion needs no rule of its own: the map is the map, and the
 * metadata go into the JPEG as they came (`lib/gainMap.ts`). What the browser
 * cannot do is hand out the auxiliary picture: `createImageBitmap` draws the
 * primary only. **The file is therefore given to the browser's own HEIF
 * decoder a second time, with the gain map as its primary** (`asPrimary()`),
 * and the decoder assembles the grid, applies the rotation and returns the
 * picture. Four places change, every other byte stays: the item number in
 * `pitm`, the map's `hidden` flag, and the type of the two references that
 * make it a part of something else, `auxl` to the primary and `dimg` from
 * the `tmap`, renamed to types no reader knows. **The `pitm` alone was not
 * enough**, measured with libheif 1.17: it showed the base picture again,
 * because a picture that is the auxiliary of another or the input of a
 * derived one is not a picture of its own to a reader. No HEVC is decoded here. Only Safari decodes HEIC,
 * and only Safari is handed one; a browser that cannot fails at the base
 * picture before this is asked.
 *
 * A HEIC without `tmap` (iOS 17 and older, Apple's own headroom only) is not
 * converted: its map follows Apple's rule and not ISO 21496-1, and a second
 * rule would be a guess. It goes out as SDR, as before.
 */
import { boxesIn } from './mp4Boxes'
import { cString, inside, itemData, itemsOf, propertiesOf, refsOf, versionOf } from './heif'

export const APPLE_GAIN_MAP = 'urn:com:apple:photo:2020:aux:hdrgainmap'

export interface HeifGainMap {
  primary: number
  gainMap: number
  /** The width and height of the gain map in the file, before its rotation. */
  mapWidth: number
  mapHeight: number
  /** The ISO 21496-1 metadata, behind the `tmap` item's version byte. */
  iso: Uint8Array
  /** The file with the gain map as its primary picture, for a HEIF decoder. */
  asPrimary(): Uint8Array<ArrayBuffer>
}

/**
 * The gain map of a HEIF, or null: no HEIF, no Apple gain map on the
 * primary, or no `tmap` that derives from both.
 */
export function heifGainMap(bytes: Uint8Array): HeifGainMap | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const top = boxesIn(view, 0, bytes.length)
  const metaBox = top?.find((b) => b.type === 'meta')
  if (metaBox === undefined) return null
  const meta = inside(view, metaBox, true)
  const pitm = meta.find((b) => b.type === 'pitm')
  const iinf = meta.find((b) => b.type === 'iinf')
  const iref = meta.find((b) => b.type === 'iref')
  const iprp = meta.find((b) => b.type === 'iprp')
  if (pitm === undefined || iinf === undefined || iref === undefined || iprp === undefined) return null
  // `pitm` version 0 carries a 16-bit id; the swap below writes in its place.
  if (versionOf(view, pitm) !== 0) return null
  const primary = view.getUint16(pitm.start + pitm.header + 4)
  const items = itemsOf(view, iinf)
  const refs = refsOf(view, iref)
  const props = propertiesOf(view, iprp)

  const auxType = (id: number): string | null => {
    const auxC = props.get(id)?.find((b) => b.type === 'auxC')
    return auxC === undefined ? null : cString(view, auxC.start + auxC.header + 4, auxC.start + auxC.size).text
  }
  const map = refs.find((r) => r.kind === 'auxl' && r.to.includes(primary) && auxType(r.from) === APPLE_GAIN_MAP)
  if (map === undefined || map.from > 0xffff) return null
  const gainMap = map.from
  const tmapItem = items.find((it) => it.type === 'tmap'
    && refs.some((r) => r.kind === 'dimg' && r.from === it.id && r.to[0] === primary && r.to[1] === gainMap))
  if (tmapItem === undefined) return null
  const data = itemData(bytes, view, meta, tmapItem.id)
  // One version byte (0), then the metadata.
  if (data === null || data.length < 6 || data[0] !== 0) return null
  const ispe = props.get(gainMap)?.find((b) => b.type === 'ispe')
  if (ispe === undefined) return null
  const mapWidth = view.getUint32(ispe.start + ispe.header + 4)
  const mapHeight = view.getUint32(ispe.start + ispe.header + 8)
  const mapItem = items.find((it) => it.id === gainMap)
  const pitmAt = pitm.start + pitm.header + 4
  const derived = refs.find((r) => r.kind === 'dimg' && r.from === tmapItem.id)
  // Unknown reference types are skipped by every reader (ISO/IEC 14496-12, 8.11.12).
  const unhook = [map.at, ...(derived === undefined ? [] : [derived.at])]

  return {
    primary,
    gainMap,
    mapWidth,
    mapHeight,
    iso: data.subarray(1),
    asPrimary() {
      const copy = new Uint8Array(new ArrayBuffer(bytes.length))
      copy.set(bytes)
      new DataView(copy.buffer).setUint16(pitmAt, gainMap)
      if (mapItem !== undefined) copy[mapItem.flagsAt] = (copy[mapItem.flagsAt] ?? 0) & ~1
      for (const at of unhook) copy.set([0x74, 0x62, 0x78, 0x78], at) // 'tbxx'
      return copy
    },
  }
}
